import { createSlice, isPending, isRejected, isFulfilled, type UnknownAction } from "@reduxjs/toolkit";
import { LoadingBarInitialState } from "../model/LoadingBarReducerType";
import { asyncReqCommentList } from "./CommentListReducer";
import { asyncReqCommunityReplyList } from "./CommunityReplyListReducer";
import { asyncReqCommentNewList } from "./CommentNewListReducer";
import { asyncReqIsLogin } from "./LoginBtnsReducer";
import { asyncReqBoardJokeNextList } from "./BoardJokeNextListReducer";

/** 공통 헬퍼 */
const loading = (s: LoadingBarInitialState, isShow: boolean) => {
  const isNativeUse = true; // 네이티브 로딩바를 사용할지 여부
  if (navigator.userAgent.indexOf(";UGGIYO_APP;") > -1 && isNativeUse) {
    // 앱 내 호출일 경우
    // 네이티브 로딩바 사용
    const AppWebView = (window as any).AppWebView;
    if (typeof (AppWebView) !== "undefined") {
      if (typeof (AppWebView.handleLoadingBar) === "function") {
        AppWebView.handleLoadingBar(isShow);
      }
    }
  }
  else {
    // 그 외
    // 리액트 로딩바 사용
    s.isShowLoadingBar = isShow;
  }
}
const inc = (s: LoadingBarInitialState) => {
  s.count += 1;
  loading(s, true); // 로딩바 제어
};
const dec = (s: LoadingBarInitialState) => {
  s.count = Math.max(0, s.count - 1);
  loading(s, s.count > 0); // 로딩바 제어
};
/** show_dim 플래그를 보는 래퍼 (특정 thunk 전용) */
const withDim =
  (handler: (s: LoadingBarInitialState) => void) =>
  (s: LoadingBarInitialState, a: { meta?: { arg?: { show_dim?: boolean } } }) => {
    const show = a.meta?.arg?.show_dim ?? true;
    if (show) handler(s);
  };

/** 특정 thunk의 액션인지 판별 */
const fromThunk = (thunk: any) => (a: UnknownAction) =>
  a.type.startsWith(thunk.pending.type.replace(/\/pending$/, "")); // pending/fulfilled/rejected 공통 prefix 매칭

const initialState: LoadingBarInitialState = {
    count: 0,
    isShowLoadingBar: false
};

const slice = createSlice({
    name: "LoadingBarReducer",
    initialState,
    reducers: {
        reset(state) {
          state.count = 0;
          loading(state, false); // 로딩바 제어
        }
    },
    extraReducers: (b) => {
        // 일부 thunk만 show_dim에 따라 증/감
        // 로그인여부
        b.addCase(asyncReqIsLogin.pending, withDim(inc));
        b.addCase(asyncReqIsLogin.fulfilled, withDim(dec));
        b.addCase(asyncReqIsLogin.rejected, withDim(dec));
        // 웃긴사진 > 댓글 목록
        b.addCase(asyncReqCommentList.pending, withDim(inc));
        b.addCase(asyncReqCommentList.fulfilled, withDim(dec));
        b.addCase(asyncReqCommentList.rejected, withDim(dec));
        // 웃긴사진 > 최신 댓글 목록
        b.addCase(asyncReqCommentNewList.pending, withDim(inc));
        b.addCase(asyncReqCommentNewList.fulfilled, withDim(dec));
        b.addCase(asyncReqCommentNewList.rejected, withDim(dec));
        // 웃긴사진 > 하단 게시물 목록
        b.addCase(asyncReqBoardJokeNextList.pending, withDim(inc));
        b.addCase(asyncReqBoardJokeNextList.fulfilled, withDim(dec));
        b.addCase(asyncReqBoardJokeNextList.rejected, withDim(dec));
        // 커뮤니티 > 댓글 목록
        b.addCase(asyncReqCommunityReplyList.pending, withDim(inc));
        b.addCase(asyncReqCommunityReplyList.fulfilled, withDim(dec));
        b.addCase(asyncReqCommunityReplyList.rejected, withDim(dec));

        // 나머지 thunk 공통 규칙
        const isSpecial = (a: UnknownAction) =>
          fromThunk(asyncReqIsLogin)(a) || // 로그인여부
          fromThunk(asyncReqCommentList)(a) || // 웃긴사진 > 댓글 목록
          fromThunk(asyncReqCommentNewList)(a) || // 웃긴사진 > 최신 댓글 목록
          fromThunk(asyncReqBoardJokeNextList)(a) || // 웃긴사진 > 하단 게시물 목록
          fromThunk(asyncReqCommunityReplyList)(a) // 커뮤니티 > 댓글 목록
          ;
        b.addMatcher((a) => isPending(a) && !isSpecial(a), (s) => inc(s));
        b.addMatcher((a) => isFulfilled(a) && !isSpecial(a), (s) => dec(s));
        b.addMatcher((a) => isRejected(a) && !isSpecial(a), (s) => dec(s));
    }
});

export const LoadingBarActions = slice.actions;
export default slice.reducer;